import { useFetch } from "@hyper-fetch/react";
import { ArrowUpRight } from "lucide-react";
import type { ReactNode } from "react";

import { NpmChip, PolarChip } from "./brand-marks";
import { TrendChart, type TrendPoint } from "./trend-chart";
import { sdk } from "@/api/sdk";
import { Skeleton } from "@/components/ui/skeleton";
import type { OpenSourceProject, SaasProject } from "@/features/portfolio/config";
import { cn } from "@/lib/utils";

const monthLabel = (isoMonth: string): string =>
  new Date(`${isoMonth}-01T00:00:00Z`).toLocaleDateString("en", { month: "short", timeZone: "UTC" });

const compact = (value: number): string =>
  Intl.NumberFormat("en", { notation: "compact", maximumFractionDigits: 1 }).format(value);

/**
 * One accent per shelf, so revenue and downloads read as different things at a
 * glance: money is chart-2 (teal), open source is chart-1. The same color
 * paints the card's chart, badge, and the shelf's header dot.
 */
export const PROJECT_ACCENTS = {
  saas: {
    chart: "hsl(var(--chart-2))",
    badge: "bg-[hsl(var(--chart-2)/0.15)] text-[hsl(var(--chart-2))]",
    chip: "bg-[hsl(var(--chart-2)/0.15)] text-[hsl(var(--chart-2))]",
  },
  openSource: {
    chart: "hsl(var(--chart-1))",
    badge: "bg-[hsl(var(--chart-1)/0.15)] text-[hsl(var(--chart-1))]",
    chip: "bg-[hsl(var(--chart-1)/0.15)] text-[hsl(var(--chart-1))]",
  },
} as const;

type ProjectCardShellProps = {
  icon: string;
  name: string;
  tagline: string;
  url: string;
  badge: string | null;
  badgeIcon: ReactNode;
  badgeClassName: string;
  children: ReactNode;
};

/** Shared card chrome: logo tile, name that links out, stat badge, chart slot. */
function ProjectCardShell({ icon, name, tagline, url, badge, badgeIcon, badgeClassName, children }: ProjectCardShellProps) {
  return (
    <div className="group rounded-2xl border border-border bg-card p-5 shadow-sm transition-shadow hover:shadow-md">
      <div className="flex items-start justify-between gap-3">
        <a href={url} target="_blank" rel="noreferrer" className="flex min-w-0 items-center gap-2.5">
          <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-accent p-1.5">
            <img src={icon} alt="" className="max-h-full max-w-full object-contain" />
          </span>
          <span className="flex min-w-0 items-center gap-1 text-lg font-semibold tracking-tight text-card-foreground">
            <span className="truncate">{name}</span>
            <ArrowUpRight className="size-3.5 shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
          </span>
        </a>
        {badge ? (
          <span
            className={cn(
              "flex shrink-0 items-center gap-1.5 rounded-full py-1 pl-1.5 pr-2.5 text-xs font-semibold",
              badgeClassName,
            )}
          >
            {badgeIcon}
            {badge}
          </span>
        ) : null}
      </div>
      <p className="mt-2 text-sm text-muted-foreground">{tagline}</p>
      <div className="mt-4">{children}</div>
    </div>
  );
}

export function SaasProjectCard({ project }: { project: SaasProject }) {
  const accent = PROJECT_ACCENTS.saas;
  // Live numbers from Polar when the backend has a token; the manual series in
  // the config is the fallback so the card never renders empty.
  const { data: live } = useFetch(sdk.portfolio.revenue.$get);

  const source = live?.available ? live.monthly : project.revenue;
  const money = (value: number): string =>
    live?.available && live.currency && live.currency !== "usd"
      ? `${compact(value)} ${live.currency.toUpperCase()}`
      : `$${compact(value)}`;

  const data: TrendPoint[] = source.map((point) => ({
    label: monthLabel(point.month),
    value: point.value,
  }));
  const latest = source.at(-1)?.value ?? 0;

  return (
    <ProjectCardShell
      icon={project.icon}
      name={project.name}
      tagline={project.tagline}
      url={project.url}
      badge={`${money(latest)}/mo`}
      badgeIcon={<PolarChip />}
      badgeClassName={accent.badge}
    >
      <TrendChart id={project.id} data={data} color={accent.chart} formatValue={(value) => `${money(value)}`} />
    </ProjectCardShell>
  );
}

export function OpenSourceProjectCard({ project }: { project: OpenSourceProject }) {
  const accent = PROJECT_ACCENTS.openSource;
  const { data, loading } = useFetch(
    sdk.portfolio.npmDownloads.$get.setQueryParams({ packages: project.packages.join(",") }),
  );

  const points: TrendPoint[] = (data?.monthly ?? []).map((point) => ({
    label: monthLabel(point.month),
    value: point.downloads,
  }));

  return (
    <ProjectCardShell
      icon={project.icon}
      name={project.name}
      tagline={project.tagline}
      url={project.url}
      badge={data ? `${compact(data.lastMonth)}/mo` : null}
      badgeIcon={<NpmChip />}
      badgeClassName={accent.badge}
    >
      {loading && !data ? <Skeleton className="h-28 w-full rounded-lg" /> : null}
      {data ? (
        <TrendChart
          id={project.id}
          data={points}
          color={accent.chart}
          formatValue={(value) => compact(value)}
        />
      ) : null}
      {!loading && !data ? (
        <p className="flex h-28 items-center justify-center text-xs text-muted-foreground">
          Download stats are unavailable right now.
        </p>
      ) : null}
    </ProjectCardShell>
  );
}
